<template>
  <div
    :key="discountId"
    class="mt-24 md:mt-32 border-sm border-solid rounded-md border-border-tertiary bg-surface-page"
  >
    <AppAccordion
      :key="discountId"
      :is-open="true"
      :only-mobile="false"
      border-color="tertiary"
      header-classes="rounded-t-md"
    >
      <template #header>
        <div class="flex gap-16 items-center px-16">
          <NuxtImg
            :src="logoSrc"
            alt=""
            class="w-28 md:w-40 text-[2.4rem] mb-0"
            loading="lazy"
            :provider="
              logoSrc?.includes('cloudfront') ? 'cloudfront' : undefined
            "
          />
          <div class="flex flex-col">
            <p class="heading-xs">{{ title }}</p>
            <p
              v-if="popupPrice && Object.keys(popupPrice).length > 0"
              class="heading-xxs"
              :aria-label="
                !!regularPrice
                  ? `Ordinarie ${regularPrice} ${productPageStore.productState.selectedDownPaymentTime === '1' ? 'kr' : 'kr/mån'}, nu ${price} ${productPageStore.productState.selectedDownPaymentTime === '1' ? 'kr' : 'kr/mån'}`
                  : `${price} ${productPageStore.productState.selectedDownPaymentTime === '1' ? 'kr' : 'kr/mån'}`
              "
            >
              <span
                class="strike-through text-text-secondary"
                aria-hidden="true"
              >
                {{ regularPrice }}
              </span>

              {{ price }}
              {{
                productPageStore.productState.selectedDownPaymentTime == '1'
                  ? 'kr'
                  : 'kr/mån'
              }}
              {{
                productPageStore.productState.selectedDownPaymentTime == '1'
                  ? ''
                  : 'i ' +
                    productPageStore.productState.selectedDownPaymentTime +
                    ' mån'
              }}
            </p>
            <p v-else class="heading-xxs">
              {{ popupPriceMonthly }}
              kr/mån
            </p>

            <p class="heading-xxs">
              {{ subTitle }}
            </p>
          </div>
        </div>
      </template>
      <div class="pb-24 px-16 space-y-8">
        <PdpCheckIconWithLabel
          v-for="(item, index) in uspItems"
          :key="index"
          :label="item.label"
          :sub-label="item.subLabel"
        />
      </div>
      <div
        v-if="
          chargingInfo &&
          chargingInfo.chargerIncluded &&
          chargingInfo.wiredChargerMinWatts &&
          chargingInfo.wiredChargerMaxWatts
        "
        class="pb-24 px-16"
      >
        <ChargingInfo
          :charger-included="chargingInfo.chargerIncluded"
          :wired-charger-min-watts="chargingInfo.wiredChargerMinWatts"
          :wired-charger-max-watts="chargingInfo.wiredChargerMaxWatts"
          :usb-pd-supported="chargingInfo.usbPdSupported"
        />
      </div>

      <div v-if="isInsurance" class="pb-25 px-16">
        <NuxtLink
          class="body-link-md text-text-success"
          to="https://www.halebop.se/.api/hbp/services/halebop-reverseproxy/v1/images/32UEHe1sK7oTGoepkoMOGx/612d5d13acbc58bceba12a18d68b3cb8/Produktblad_IPID_Halebop_Forsakring_2022-03-10.pdf"
          target="_blank"
          external
        >
          Försäkring Produktfaktablad
        </NuxtLink>
      </div>
    </AppAccordion>

    <div
      class="bg-surface-primary flex justify-between rounded-b-md pt-14 pb-22 py-16 px-16"
    >
      <InputCheckbox
        v-model="checked"
        :label="isInsurance ? 'Ja tack, jag godkänner' : 'Ja tack, lägg till'"
      />
      <NuxtLink
        v-if="isInsurance"
        class="body-link-md text-text-success flex items-center"
        to="https://www.halebop.se/.api/hbp/services/halebop-reverseproxy/v1/images/XfqJxYYvpxt8vURDJMdbo/03140d6b1d1ac3b1e0c3869420ccb24d/20230601_halebop_produktfo__rsa__kring_villkor.pdf"
        target="_blank"
        external
      >
        Fullständiga villkor
      </NuxtLink>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { AggregatedPopupPriceDto } from '~/models/ListingProduct.api.model';

interface Props {
  discountId: number;
  title: string;
  subTitle: string;
  imageUrl: string;
  displayOrder?: number;
  popupPriceMonthly: number;
  popupPrice: { [key: number]: AggregatedPopupPriceDto };
  shortDescription: string;
  uspItems?: { label: string; subLabel?: string }[];
  chargingInfo?: {
    chargerIncluded: boolean;
    wiredChargerMinWatts: string;
    wiredChargerMaxWatts: string;
    usbPdSupported?: boolean;
  };
}
const props = defineProps<Props>();
const config = useRuntimeConfig();

const isInsurance = computed(() =>
  props.title.toLowerCase().includes('försäkring'),
);

const productPageStore = useMyProductPageStore();

const regularPrice = computed(() => {
  if (Object.keys(props.popupPrice).length !== 0) {
    return props.popupPrice[
      Number(productPageStore.productState.selectedDownPaymentTime)
    ]?.RegularPrice;
  }
  return '';
});

const price = computed(() => {
  if (Object.keys(props.popupPrice).length !== 0) {
    return props.popupPrice[
      Number(productPageStore.productState.selectedDownPaymentTime)
    ]?.Price;
  }
  return '';
});

const logoSrc = computed(() => {
  if (!isInsurance.value) {
    return props.imageUrl.length
      ? props.imageUrl
      : `${config.public.publicCloudfrontDistribution}/images/logos/symbol_logo_thumb.png`;
  }

  return `${config.public.publicCloudfrontDistribution}/images/logos/symbol_logo_thumb.png`;
});

const checked: Ref<boolean> = ref(false);

watch(checked, (value: boolean) => {
  if (value == true) {
    productPageStore.productState.addedPopupDiscounts.push(props.discountId);
  } else {
    const index = productPageStore.productState.addedPopupDiscounts.indexOf(
      props.discountId,
    );
    if (index > -1) {
      productPageStore.productState.addedPopupDiscounts.splice(index, 1);
    }
  }
});
</script>
